// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/PaymentsCard.jsx
// *******************************************************************************
// INVOICE PAYMENTS CARD
// *******************************************************************************
// Displays payment methods with add/remove functionality.
// Exposes add handler via onActionsChange for Collapse header button.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect } from 'react';
import {
  Typography,
  Descriptions,
  Divider,
  Button,
  Tag,
  Popconfirm,
  Alert,
  Space,
  Flex,
  theme,
} from 'antd';

const { useToken } = theme;
import { DeleteOutlined } from '@ant-design/icons';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService, { PAYMENT_INDICATOR, INVOICE_PURPOSE } from 'services/fiscalService';
import PaymentModal from '../../modals/PaymentModal';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PAYMENT_INDICATOR_OPTIONS = [
  { value: PAYMENT_INDICATOR.CASH, labelKey: 'payment_cash' },
  { value: PAYMENT_INDICATOR.INSTALLMENT, labelKey: 'payment_installment' },
  { value: PAYMENT_INDICATOR.OTHER, labelKey: 'payment_other_indicator' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PaymentsCard = ({
  items = [],
  payments = [],
  onAddPayment,
  onRemovePayment,
  onActionsChange,
  viewMode,
  data,
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { token } = useToken();

  // 3. LOCAL VARIABLES I
  const isLocked = viewMode === 'view';
  const isAuthorized = data?.status === 'authorized';
  const isNoPaymentPurpose =
    data?.purpose === INVOICE_PURPOSE.COMPLEMENTARY ||
    data?.purpose === INVOICE_PURPOSE.RETURN ||
    data?.purpose === INVOICE_PURPOSE.ADJUSTMENT;

  const paymentIndicatorOptions = useMemo(
    () => PAYMENT_INDICATOR_OPTIONS.map(opt => ({ value: opt.value, label: translateX(opt.labelKey) })),
    []
  );

  // 4. LOCAL STATE
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // 5. EFFECTS

  // Expose add handler to parent for Collapse header button
  useEffect(() => {
    onActionsChange?.({ handleAddPaymentClick });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActionsChange]);

  // 6. HANDLERS
  const handleAddPaymentClick = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = paymentData => {
    onAddPayment(paymentData);
    setShowPaymentModal(false);
  };

  // 7. LOCAL VARIABLES II
  const totalsInvoiceTotal = useMemo(() => {
    if (data?.totals) return data.totals.documentAmount || 0;
    return items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  }, [items, data?.totals]);

  const paymentsTotal = useMemo(() => {
    return payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  }, [payments]);

  const balance = useMemo(() => {
    return financialService.calculateChange(totalsInvoiceTotal, paymentsTotal);
  }, [totalsInvoiceTotal, paymentsTotal]);

  const hasChange = balance > 0;
  const hasRemaining = balance < 0;
  const isPaid = balance === 0 && paymentsTotal > 0;

  // 8. UI
  if (isNoPaymentPurpose) {
    return (
      <Alert
        type="info"
        showIcon
        title={translateX('no_payment_applicable')}
        description={translateX(
          data?.purpose === INVOICE_PURPOSE.COMPLEMENTARY
            ? 'no_payment_complementary_description'
            : data?.purpose === INVOICE_PURPOSE.ADJUSTMENT
              ? 'no_payment_adjustment_description'
              : 'no_payment_return_description'
        )}
      />
    );
  }

  return (
    <>
      <SimpleSegment
        name={['payment', 'indicator']}
        label="payment_indicator"
        options={paymentIndicatorOptions}
        disabled={isLocked || isAuthorized}
        style={{ marginBottom: 16 }}
      />

      <Divider style={{ margin: '12px 0' }} />

      {payments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Text type="secondary">{translateX('no_payments_added')}</Text>
        </div>
      ) : (
        <div>
          {payments.map((payment, index) => {
            const paymentType = fiscalService.getPaymentTypeByCode(payment.code);
            const isLast = index === payments.length - 1;
            const rowStyle = { padding: '8px 0', borderBottom: isLast ? 'none' : `1px solid ${token.colorBorderSecondary}` };
            return (
              <Flex key={index} justify="space-between" align="center" style={rowStyle}>
                <div>
                  <Space>
                    <Tag>{payment.code}</Tag>
                    <Text>{translateX(paymentType?.labelKey || payment.type)}</Text>
                  </Space>
                  <div>
                    <Text type="secondary">
                      {financialService.formatCurrency(payment.amount)}
                    </Text>
                  </div>
                </div>
                {!isLocked && !isAuthorized && (
                  <Popconfirm
                    title={translateX('remove_payment_confirm')}
                    onConfirm={() => onRemovePayment(index)}
                    okText={translateX('yes')}
                    cancelText={translateX('no')}
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Flex>
            );
          })}
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text>{translateX('total_paid')}:</Text>
        <Text strong>{financialService.formatCurrency(paymentsTotal)}</Text>
      </div>

      {hasRemaining && (
        <Alert
          type="warning"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{translateX('remaining_balance')}:</span>
              <Text strong>{financialService.formatCurrency(Math.abs(balance))}</Text>
            </div>
          }
          showIcon
        />
      )}

      {hasChange && (
        <Alert
          type="info"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{translateX('change')}:</span>
              <Text strong>{financialService.formatCurrency(balance)}</Text>
            </div>
          }
          showIcon
        />
      )}

      {isPaid && (
        <Alert type="success" title={translateX('payment_complete')} showIcon />
      )}

      <PaymentModal
        show={showPaymentModal}
        setShow={setShowPaymentModal}
        onConfirm={handlePaymentConfirm}
        invoiceTotal={totalsInvoiceTotal}
        paidAmount={paymentsTotal}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PaymentsCard;
